// onboardingWizard.js — THE SETUP WIZARD of the new-account welcome
// (Nika 2026-10-18: "onboarding should not be just chat assistant. it should
// be wizard like onboarding and click based onboarding could be possible.
// this will help to personalize the view and avoid showing complex ui at
// first load").
//
// A first-run surface that covers the whole shell (components/
// OnboardingWizard.js) and walks a new member through a few CLICK steps, each
// a real choice written through the writers Customize dashboard uses:
//
//   welcome   the greeting — Get started · Set up with the assistant · Take
//             the quick tour
//   work      Your work — a Workspace Preset or Something else (an owner/admin
//             of the workspace, while the SDK can apply presets); the ✓ row
//             when the workspace is set up already (functions/
//             onboardingPresets.js — the three choices, wt/presets CONTRACT §1)
//   stack     Your apps — the picked work's apps; leaving it forward sends the
//             ONE apply in the background
//   idea      Your idea — one optional sentence for the app builder
//   apps      the apps the home shows (not in a flow that shows `stack`): the
//             App strip keeps the picked ones, the others stay in All apps
//             (pages/main.js `_homeOps`)
//   look      theme and background
//   home      the summary (Brief | Signals) and the App strip (Dock | Rail |
//             off)
//   done      Start working (Build my app with an idea) · Take the quick tour
//             · Ask the assistant — and the lines of what was set up
//   ready     a person who signed up through Fly meets this ONE step instead
//             of all the others (its flow has no other step): Your workspace
//             is ready, what Fly made, Start working · Take the quick tour
//             (functions/onboardingReady.js)
//
// ONE VOCABULARY with the setup conversation: the options are its plan's
// (functions/onboarding.js `onboardingPlan`) and every pick applies AT ONCE
// through its writer (`onboardingApply` — the theme repaints the wizard
// itself, the rest lands on the home behind it). The apps are the exception:
// their picks are held in the flow and written in ONE pass when the step is
// left forward, so a dozen toggles are one save.
//
// STATE (`root._onboardingFlow`): `step` names the step; `picks.apps` holds
// the app picks; `picked` the choices made here (a tile shows its pick before
// the writer's state lands); `progressed` turns true at the first pick or the
// first step left forward — a Skip after that counts as done
// (functions/onboarding.js closeOnboarding).
//
// FRANK: every export is self-contained (FA204); siblings through `this.call`.

// The steps for this member, in order — TWO SIDES, never mixed. First the
// WORKSPACE side: the three choices of the Workspace Presets program, for an
// owner/admin of the workspace, written for everyone in it — `work` while the
// host can apply presets, `stack` once a work is picked (never over a
// workspace set up already), `idea`. Then the PERSONAL side: this person's
// own choices only (shared settings such as the workspace language live in
// workspace settings) — `apps` only when the board offers a choice (two apps
// or more), and never in a flow that shows `stack`: every app the person just
// picked shows on the home anyway, and a second "pick your apps" reads as a
// bug (one screen per choice); then the look and the home.
export function onboardingWizardSteps(root) {
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  const flow = r && r._onboardingFlow
  // Fly's ready screen is a flow of ONE step: nothing to walk, no counter, no
  // journey (functions/onboardingReady.js).
  if (flow && flow.step === 'ready') return ['ready']
  const plan = el.call('onboardingPlan', r) || []
  const has = (id) => plan.some((q) => q && q.id === id)
  const steps = ['welcome']
  const mode = el.call('onboardingPresetMode', r)
  const stack = !!(mode === 'pick' && flow && flow.picks && flow.picks.work)
  if (mode) steps.push('work')
  if (stack) steps.push('stack')
  if (el.call('onboardingIdeaAsked', r)) steps.push('idea')
  const apps = el.call('onboardingWizardApps', r)
  if (!stack && Array.isArray(apps) && apps.length > 1) steps.push('apps')
  if (has('theme') || has('sky')) steps.push('look')
  if (has('summary') || has('strip')) steps.push('home')
  steps.push('done')
  return steps
}

// Where the flow stands: { step, n, total } — `n` / `total` count the middle
// steps only (the welcome, the done step and Fly's ready screen carry no
// counter, nor does the conversation): n is 0 there.
export function onboardingWizardAt(root) {
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  const flow = r && r._onboardingFlow
  if (!flow) return { step: null, n: 0, total: 0 }
  const steps = el.call('onboardingWizardSteps', r) || []
  const middle = steps.filter((s) => s !== 'welcome' && s !== 'done' && s !== 'ready')
  const at = middle.indexOf(flow.step)
  return { step: flow.step, n: at < 0 ? 0 : at + 1, total: middle.length }
}

// Move one step (+1 forward, -1 back). Leaving the apps step forward writes
// its held picks; leaving Your work forward writes the first workspace's
// typed name (onboardingNameSave); leaving Your apps forward sends the work's ONE apply (in
// the background — the flow goes on); leaving any middle step forward marks
// the flow progressed. The new step's heading takes the focus
// (onboardingWizardFocus). Returns the step landed on, or null.
export function onboardingWizardGo(delta) {
  const el = this
  const root = el && el.state && el.state.root
  const flow = root && root._onboardingFlow
  if (!flow || typeof root.update !== 'function') return null
  const steps = el.call('onboardingWizardSteps', root) || []
  const at = steps.indexOf(flow.step)
  // The conversation steps back to the welcome; any unknown step starts it.
  const from = at < 0 ? 0 : at
  const to =
    at < 0 && delta < 0 ? 0 : Math.max(0, Math.min(steps.length - 1, from + (delta < 0 ? -1 : 1)))
  if (at >= 0 && to === at) return flow.step
  const forward = at >= 0 && to > at
  if (forward && flow.step === 'apps') el.call('onboardingWizardApplyApps')
  if (forward && flow.step === 'work') el.call('onboardingNameSave')
  if (forward && flow.step === 'stack') el.call('onboardingStackSend')
  const middle = flow.step !== 'welcome' && flow.step !== 'done' && flow.step !== 'assistant'
  const now = root._onboardingFlow || flow
  root.update(
    {
      _onboardingFlow: {
        ...now,
        step: steps[to],
        progressed: !!(now.progressed || (forward && middle))
      }
    },
    { preventFetch: true }
  )
  el.call('onboardingWizardFocus')
  return steps[to]
}

// The new step's heading takes the focus, so a screen reader reads where the
// person is and Tab starts at the step's own controls. After the hide flips
// (a hidden heading cannot take focus — the show lands in a microtask), from
// the app root (the button that asked may be gone by then).
export function onboardingWizardFocus() {
  const el = this
  const host = (el && typeof el.getRoot === 'function' && el.getRoot()) || el
  setTimeout(() => {
    try {
      const layer = host && typeof host.lookdown === 'function' && host.lookdown('OnboardingWizard')
      const title = layer && typeof layer.lookdown === 'function' && layer.lookdown('WizardTitle')
      const node = title && title.node
      if (node && node.isConnected !== false && typeof node.focus === 'function') {
        node.focus({ preventScroll: true })
      }
    } catch (_) {
      /* focus is a courtesy — never an error */
    }
  }, 0)
}

// The value a question shows as chosen: this wizard's own pick first, then
// the live state — the workspace's language, the person's theme and look.
export function onboardingWizardCurrent(id) {
  const el = this
  const root = el && el.state && el.state.root
  if (!root) return null
  const flow = root._onboardingFlow
  const picked = flow && flow.picked && typeof flow.picked === 'object' ? flow.picked : {}
  if (typeof picked[id] === 'string') return picked[id]
  if (id === 'language') return (el.call('onboardingLanguage', root) || {}).current || null
  if (id === 'theme') {
    const mode = root.themeMode
    return mode === 'light' || mode === 'dark' ? mode : 'auto'
  }
  const look = el.call('homeLookRead', root) || {}
  if (id === 'summary') return look.summaryStyle === 'signals' ? 'signals' : 'brief'
  if (id === 'strip') {
    if (look.stripShown === false) return 'off'
    return look.stripStyle === 'rail' ? 'rail' : 'dock'
  }
  return null
}

// A pick on a question's tile: applied at once through the conversation's
// own writer (`onboardingApply`), and held as this wizard's pick.
export function onboardingWizardPick(id, value) {
  const el = this
  const root = el && el.state && el.state.root
  const flow = root && root._onboardingFlow
  if (!flow || typeof root.update !== 'function' || typeof value !== 'string') return false
  const picked = flow.picked && typeof flow.picked === 'object' ? flow.picked : {}
  root.update(
    { _onboardingFlow: { ...flow, progressed: true, picked: { ...picked, [id]: value } } },
    { preventFetch: true }
  )
  el.call('onboardingApply', id, value)
  return true
}

// A starting style is a real home preference, not a profile questionnaire.
// One write keeps the summary and shortcuts together; the detailed controls
// remain available below, and changing either clears the matching preset.
export function onboardingWizardPreset(value) {
  const root = this.state && this.state.root
  const flow = root && root._onboardingFlow
  if (!flow || !['focus', 'overview'].includes(value)) return false
  const summary = value === 'overview' ? 'signals' : 'brief'
  root.update(
    {
      _onboardingFlow: {
        ...flow,
        progressed: true,
        picked: { ...flow.picked, summary, strip: 'dock' }
      }
    },
    { preventFetch: true }
  )
  this.call('homeLookSet', { summaryStyle: summary, stripShown: true, stripStyle: 'dock' })
  return true
}

// Short, contextual lessons: the person learns one useful thing while making
// a choice. Literal catalog keys survive both Frank and the translation census.
// The title heads the studio preview's caption; the icon + text are the
// lesson line under the step (OnboardingLesson). The workspace side's three
// choices (work, stack, idea) keep a title — the caption says the part is for
// the whole workspace — but no text: each says its one thing in its lead (say
// it once), and OnboardingLesson stays hidden there. Every step answers an
// icon, so the hidden lesson's Icon never reads an empty name.
export function onboardingWizardLesson(root) {
  const step = (root || this.state.root)?._onboardingFlow?.step || 'welcome'
  const lessons = {
    welcome: [
      'magicstar',
      'workspace.onboarding.learn.welcomeTitle',
      'workspace.onboarding.learn.welcomeText'
    ],
    work: ['layers', 'workspace.onboarding.learn.workTitle'],
    stack: ['blocks', 'workspace.onboarding.learn.stackTitle'],
    idea: ['magicstar', 'workspace.onboarding.learn.ideaTitle'],
    apps: ['search', 'workspace.onboarding.learn.appsTitle', 'workspace.onboarding.learn.appsText'],
    look: [
      'magicstar',
      'workspace.onboarding.learn.lookTitle',
      'workspace.onboarding.learn.lookText'
    ],
    home: ['home', 'workspace.onboarding.learn.homeTitle', 'workspace.onboarding.learn.homeText'],
    done: [
      'checkmark',
      'workspace.onboarding.learn.doneTitle',
      'workspace.onboarding.learn.doneText'
    ],
    // Fly's ready screen: the studio's caption wears the done step's title;
    // the lesson line stays hidden there (it says its one thing in its lead).
    ready: ['checkmark', 'workspace.onboarding.learn.doneTitle'],
    assistant: [
      'magicstar',
      'workspace.onboarding.learn.assistantTitle',
      'workspace.onboarding.learn.assistantText'
    ]
  }
  const lesson = lessons[step] || lessons.welcome
  return { icon: lesson[0], title: lesson[1], text: lesson[2] || '' }
}

// TWO SIDES in the one progress indicator: the workspace's three choices,
// then the person's own steps — each side named once, at its first stop, when
// the flow has both (a member's journey is the personal side alone).
export function onboardingWizardJourney(root) {
  const r = root || this.state.root
  const steps = this.call('onboardingWizardSteps', r) || []
  const current = steps.indexOf(r?._onboardingFlow?.step)
  const labels = {
    work: 'workspace.onboarding.journey.work',
    stack: 'workspace.onboarding.journey.stack',
    idea: 'workspace.onboarding.journey.idea',
    apps: 'workspace.onboarding.journey.apps',
    look: 'workspace.onboarding.journey.look',
    home: 'workspace.onboarding.journey.home'
  }
  const sideOf = (step) =>
    ['work', 'stack', 'idea'].indexOf(step) !== -1 ? 'workspace' : 'personal'
  const shown = steps.filter((step) => labels[step])
  const both =
    shown.some((step) => sideOf(step) === 'workspace') &&
    shown.some((step) => sideOf(step) === 'personal')
  return shown.map((step, i) => ({
    key: step,
    label: labels[step],
    number: i + 1,
    status:
      steps.indexOf(step) < current
        ? 'complete'
        : steps.indexOf(step) === current
          ? 'current'
          : 'upcoming',
    side: sideOf(step),
    sideLabel:
      both && (i === 0 || sideOf(shown[i - 1]) !== sideOf(step))
        ? sideOf(step) === 'workspace'
          ? 'workspace.onboarding.journey.sideWorkspace'
          : 'workspace.onboarding.journey.sidePersonal'
        : ''
  }))
}

// The apps the apps step offers: the board's own rows (pages/main.js
// `_homeOps.onboardingApps` — the App strip's apps, shown or restorable), each
// with this wizard's held pick over its live state. [] off the home.
export function onboardingWizardApps(root) {
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  const ops = r && r._homeOps
  const rows = ops && typeof ops.onboardingApps === 'function' ? ops.onboardingApps() : []
  const flow = r && r._onboardingFlow
  const picks = (flow && flow.picks && flow.picks.apps) || {}
  return (Array.isArray(rows) ? rows : []).map((row) => ({
    // A row key never equal to a tag name (an app id may be `video`), never
    // the data's own fields.
    key: 'pick-' + row.id,
    id: row.id,
    title: row.title,
    icon: row.icon,
    openRoute: row.openRoute,
    picked: typeof picks[row.id] === 'boolean' ? picks[row.id] : row.shown !== false
  }))
}

// Flip one app's pick (held until the step is left forward).
export function onboardingWizardToggleApp(id) {
  const el = this
  const root = el && el.state && el.state.root
  const flow = root && root._onboardingFlow
  if (!flow || typeof root.update !== 'function' || !id) return false
  const row = (el.call('onboardingWizardApps', root) || []).find((r) => r.id === id)
  if (!row) return false
  const picks = (flow.picks && flow.picks.apps) || {}
  root.update(
    {
      _onboardingFlow: {
        ...flow,
        progressed: true,
        picks: { ...(flow.picks || {}), apps: { ...picks, [id]: !row.picked } }
      }
    },
    { preventFetch: true }
  )
  return true
}

// Pick every app (`true`) or none (`false`).
export function onboardingWizardAllApps(on) {
  const el = this
  const root = el && el.state && el.state.root
  const flow = root && root._onboardingFlow
  if (!flow || typeof root.update !== 'function') return false
  const apps = {}
  for (const row of el.call('onboardingWizardApps', root) || []) apps[row.id] = !!on
  root.update(
    { _onboardingFlow: { ...flow, progressed: true, picks: { ...(flow.picks || {}), apps } } },
    { preventFetch: true }
  )
  return true
}

// Write the held app picks — only the apps whose pick differs from what the
// home shows now — in one pass (`_homeOps.setAppsShown`).
export function onboardingWizardApplyApps() {
  const el = this
  const root = el && el.state && el.state.root
  const ops = root && root._homeOps
  const flow = root && root._onboardingFlow
  const picks = flow && flow.picks && flow.picks.apps
  if (!ops || typeof ops.setAppsShown !== 'function' || !picks) return false
  const live = typeof ops.onboardingApps === 'function' ? ops.onboardingApps() || [] : []
  const change = {}
  for (const row of live) {
    if (typeof picks[row.id] !== 'boolean') continue
    if (picks[row.id] !== (row.shown !== false)) change[row.id] = picks[row.id]
  }
  if (!Object.keys(change).length) return false
  return ops.setAppsShown(change) !== false
}

// The ways out — 'home' (Start working), 'tour', 'assistant', 'build' (Build
// my app: the app builder opens on the idea — parked, the route bare) and
// 'store' (Your apps' Browse the App Store). A finished wizard is a decision
// (done, via the wizard) unless it is a replay; the wizard closes — a work
// pick not sent yet goes then — and the way runs from the app root once the
// layer has left (the tour measures a still page; the composer takes focus).
export function onboardingWizardFinish(next) {
  const el = this
  const root = el && el.state && el.state.root
  const flow = root && root._onboardingFlow
  if (!flow) return false
  const idea = String((flow.picks && flow.picks.idea) || '')
    .replace(/\s+/g, ' ')
    .trim()
  if (!flow.replay) el.call('onboardingDecide', 'done', 'wizard')
  el.call('closeOnboarding', 'finish')
  const host = (typeof el.getRoot === 'function' && el.getRoot()) || el
  const fns = (host && host.context && host.context.functions) || {}
  if (next === 'build' && idea) setTimeout(() => host.call('onboardingBuildIdea', idea), 320)
  else if (next === 'store') {
    setTimeout(() => host.call('openAppPage', '/marketplace?surface=appstore'), 320)
  } else if (next === 'tour') setTimeout(() => host.call('onboardingTourStart'), 320)
  else if (next === 'assistant' && typeof fns.assistantBoardFocus === 'function') {
    setTimeout(() => host.call('assistantBoardFocus', 'composer'), 320)
  } else if (next === 'home') {
    setTimeout(() => host.call('onboardingExitToComposer', idea), 320)
  }
  return true
}

// Skip keeps a typed idea available without submitting it. Move focus only
// after the setup layer has left, and leave any existing draft intact.
export function onboardingExitToComposer(idea) {
  const el = this
  const root = el && el.state && el.state.root
  if (!root || root._onboardingOpen || root._onboardingHold) return false
  try {
    const host = (typeof el.getRoot === 'function' && el.getRoot()) || el
    const composer = host && host.lookdown && host.lookdown('CanvasPromptTextarea')
    const field = composer && composer.lookdown && composer.lookdown('Textarea')
    const node = field && field.node
    if (!node || node.isConnected === false) return false
    const text = String(idea || '').trim()
    if (text && composer.state && typeof composer.state.update === 'function') {
      const current = String(composer.state.draft || '')
      const draft = current.trim() && current.trim() !== text ? current + '\n' + text : text
      composer.state.update({ draft })
    }
    node.focus({ preventScroll: true })
    return true
  } catch (_) {
    return false
  }
}
